// Libraries
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

// Core
import { ResourceTextApplication } from '../../core/resources';

// Feature
import ADialog from '../../common/dialog/ADialog';
import ADialogTitle from '../../common/dialog/ADialogTitle';
import CloseDialogButton from '../announcements/components/general/CloseDialogButton';
import ADialogContent from '../../common/dialog/ADialogContent';
import {
    IFarmQuestion,
    IFarmSupplier,
    IFarmTrend,
    IPlanAnimalData,
    IPlanConfirmationData,
    IPlanFarmBaseInfo,
    IPlanFarmBaseInfoErrors,
    IProductionPlan,
    IProductionPlanAccess,
    IProductionPlanUserAccess,
} from '../../interfaces/IProductionPlan';
import productionPlanApi from '../../api/productionPlanApi';
import { IFarmDetails } from '../../interfaces/IBusinessEntities';
import FormValidator from '../../core/FormValidator';

// Dialog blocks
import BasicFarmInfo from './comonents/BasicFarmInfo';
import PlanHeaderInfo from './comonents/PlanHeaderInfo';
import AnimalDataGrids from './comonents/AnimalDataGrids';
import FarmTrends from './comonents/FarmTrends';
import Environment from './comonents/Environment';
import Confirmation from './comonents/Confirmation';
import { ITiltuApiResult } from '../../interfaces/IGeneral';
import { AnimalTypeEnum, FarmingTypeEnum, OrganicProductionState, ProductionPlanAnimalType, ProductionPlanQuestionType, ProductionPlanType, QuestionResourceText } from '../../interfaces/enums';
import companyApi from '../../api/companyApi';
import '../shared/styles/sharedStyles.scss';
import EndingOfProduction from './comonents/EndingOfProduction';
import { useAppSelector } from '../../store/hooks';
import { newRandomGuid } from './helpers/FarmSupplierHelper';

interface IProductionPlanProps {
    data: IProductionPlan;
    access: IProductionPlanUserAccess;
    farmDetails: IFarmDetails | null;
    readonly?: boolean;
    onClose: (reload?: boolean) => void;
    isNew?: boolean;
}

export default function ProductionPlan(props: IProductionPlanProps) {
    const { data } = props;
    const { t } = useTranslation<ResourceTextApplication[]>([
        'AnelmaGeneral',
        'AnelmaCattleProductionPlan',
    ]);
    const { enqueueSnackbar } = useSnackbar();
    const userData = useAppSelector((state) => state.userData.data);
    const validator = new FormValidator();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [readonly, setReadonly] = useState(false);
    const [access, setAccess] = useState<IProductionPlanAccess | null>(null);
    const [farmDetails, setFarmDetails] = useState<IFarmDetails | null>(null);
    const [farmTrendsOpened, setFarmTrendsOpened] = useState<boolean>(false);

    const getAnimalQuestions = (q: IFarmQuestion) => {
        switch (q.ResourceId) {
            case QuestionResourceText.NoTransmissionBovineSold.toString():
            case QuestionResourceText.NoSlaughterBovineSold.toString():
                return true;
            default:
                return false;
        }
    }
    const [animalData, setAnimalData] = useState<IPlanAnimalData>({
        Questions: data.FarmQuestions.filter(getAnimalQuestions),
        AnimalCapacities: data.AnimalCapacities,
        PreviousTransmissionAnimalsSold: data.PreviousTransmissionAnimalsSold,
        PreviousTransmissionAnimalsNeeded: data.PreviousTransmissionAnimalsNeeded,
        PreviousSlaughterBovineSold: data.PreviousSlaughterBovineSold,
        PlannedTransmissionAnimalsSold: data.PlannedTransmissionAnimalsSold,
        PlannedTransmissionAnimalsNeeded: data.PlannedTransmissionAnimalsNeeded,
        PlannedSlaughterBovineSold: data.PlannedSlaughterBovineSold,
        BovineRegisterDate: data.BovineRegisterDate,
    });
    const [capacityError, setCapacityError] = useState<boolean>(false);

    const getFarmBaseQuestions = (q: IFarmQuestion) => {
        switch (q.ResourceId) {
            case QuestionResourceText.CowsAreInCowshed.toString():
            case QuestionResourceText.LimousineMembershipExists.toString():
            case QuestionResourceText.PrintedClearanceRequested.toString():
            case QuestionResourceText.OrganicFarmingPerformed.toString():
            case QuestionResourceText.BuysCalfsDirectlyFromOtherFarm.toString():
            case QuestionResourceText.HasSalmonellaInsurance.toString():
            case QuestionResourceText.SoftSleepingPad.toString():
            case QuestionResourceText.PlanToIncreaseAnimalAmount.toString():
                return true;
            default:
                return false;
        }
    }
    const [farmBaseData, setFarmBaseData] = useState<IPlanFarmBaseInfo>({
        Questions: data.FarmQuestions.filter(getFarmBaseQuestions),
        SellingFarms: data.SellingFarms,
        SellingFarmsQuestions: data.SellingFarmQuestions,
    });
    const [farmBaseDataErrors, setFarmBaseDataErrors] = useState<IPlanFarmBaseInfoErrors>({
        organicProductionError: false,
        calfBuyError: false,
        salmonellaSampleError: false,
        sleepingPadError: false,
    });

    const [farmTrends, setFarmTrends] = useState<IFarmTrend[]>(data.FarmTrends);
    const [farmTrendsError, setFarmTrendsError] = useState<boolean>(false);

    const getEnvironmentQuestions = (q: IFarmQuestion) => {
        switch (q.ResourceId) {
            case QuestionResourceText.YearlyLawnCropAmount.toString():
            case QuestionResourceText.CommittedToEnviromentalSupportConditions.toString():
            case QuestionResourceText.IsBiogasProducer.toString():
            case QuestionResourceText.IsSolarPowerProducer.toString():
            case QuestionResourceText.IsWindPowerProducer.toString():
            case QuestionResourceText.AdditionalInfo.toString():
                return true;
            default:
                return false;
        }
    }
    const [environmentQuestions, setEnvironmentQuestions] = useState<IFarmQuestion[]>(data.FarmQuestions.filter(getEnvironmentQuestions));

    const getEndingOfProductionQuestions = (q: IFarmQuestion) => {
        switch (q.ResourceId) {
            case QuestionResourceText.ProductionEndYear.toString():
                return true;
            default:
                return false;
        }
    }
    const [endingOfProductionQuestions, setEndingOfProductionQuestions] = useState<IFarmQuestion[]>(data.FarmQuestions.filter(getEndingOfProductionQuestions));

    const [confirmationData, setConfirmationData] = useState<IPlanConfirmationData>({ ...data });

    useEffect(() => {
        if (props.access.IsAdmin) {
            setReadonly(false);
        } else {
            setReadonly(props.readonly != null ? props.readonly : false);
        }
        if (props.farmDetails) setFarmDetails(props.farmDetails);
        else if (props.access.IsAdmin)
            companyApi.getCompanyByGuid(props.data.FarmId).then((response) => {
                setFarmDetails(response?.Entity as IFarmDetails);
            });
    }, [props.data, props.readonly]);

    useEffect(() => {
        if (!farmDetails) return;
        setAccess({
            ...props.access,
            ProductionDirection:
                farmDetails.ProductionLines.find((x) => x.Type === AnimalTypeEnum.Bovine)
                    ?.FarmingTypes || [],
            HasDairyAnimals:
                animalData.AnimalCapacities.filter(
                    (x) => x.AnimalType === ProductionPlanAnimalType.DairyCow
                ).length > 0,
            HasBreedingAnimals:
                animalData.AnimalCapacities.filter(
                    (x) => x.AnimalType === ProductionPlanAnimalType.BreedingCow
                ).length > 0,
        });
        setIsLoading(false);
    }, [props.access, farmDetails, animalData.AnimalCapacities]);

    const handleSaveResponse = (response: ITiltuApiResult<IProductionPlan> | null): boolean => {
        if (!response) {
            enqueueSnackbar(t('AnelmaGeneral:1019'), {
                variant: 'error',
            });
            return false;
        } else if (
            response.Summary &&
            response.Summary.Errors &&
            response.Summary.Errors.length > 0
        ) {
            for (let error of response.Summary.Errors) {
                enqueueSnackbar(t(`${error.Explanation}`), {
                    variant: 'error',
                });
            }
            return false;
        }
        enqueueSnackbar(t('AnelmaGeneral:1018'), {
            variant: 'success',
        });
        return true;
    };

    const closeAfterSave = (success: boolean) => {
        if (success) props.onClose(true);
    };

    const handleSaveButtonClick = () => {
        if (readonly) return;

        setIsLoading(true);
        if (isFarmBaseAnswersValid() && 
            isAnimalCapacitiesValid() &&
            isFarmTrendsOpened() &&
            validator.valid
        ) {
            let plan: IProductionPlan = {
                ...data,
                ...confirmationData,
                Id: data.Id,
                AnimalCapacities: animalData.AnimalCapacities,
                PreviousTransmissionAnimalsSold: animalData.PreviousTransmissionAnimalsSold,
                PreviousTransmissionAnimalsNeeded: animalData.PreviousTransmissionAnimalsNeeded,
                PreviousSlaughterBovineSold: animalData.PreviousSlaughterBovineSold,
                PlannedTransmissionAnimalsSold: animalData.PlannedTransmissionAnimalsSold,
                PlannedTransmissionAnimalsNeeded: animalData.PlannedTransmissionAnimalsNeeded,
                PlannedSlaughterBovineSold: animalData.PlannedSlaughterBovineSold,
                FarmTrends: farmTrends,
                FarmQuestions: farmBaseData.Questions.concat(animalData.Questions, environmentQuestions, endingOfProductionQuestions),
                SellingFarms: farmBaseData.SellingFarms,
                SellingFarmQuestions: data.SellingFarmQuestions,
            }

            if (!plan.Id)
                productionPlanApi
                    .createPlan(plan)
                    .then(handleSaveResponse)
                    .then(closeAfterSave)
                    .finally(() => setIsLoading(false));
            else
                productionPlanApi
                    .updatePlan(plan)
                    .then(handleSaveResponse)
                    .then(closeAfterSave)
                    .finally(() => setIsLoading(false));
        }
    };

    const isFarmBaseAnswersValid = () => {
        return isBuysCalfsDirectlyFromOtherFarmValid() &&
            isOrganicProductionStateValid() &&
            requiredFarmBaseAnswersSet();
    }

    const isOrganicProductionStateValid = () => {
        let organicProductionQuestionIndex =
            farmBaseData.Questions.findIndex(q =>
                q.ResourceId === QuestionResourceText.OrganicFarmingPerformed.toString()
                && q.Type === ProductionPlanQuestionType.Boolean);

        if (organicProductionQuestionIndex !== -1
            && farmBaseData.Questions[organicProductionQuestionIndex].Value === '1') {
            let organicProductionStateQuestionIndex =
                farmBaseData.Questions.findIndex(q =>
                    q.ResourceId === QuestionResourceText.OrganicFarmingPerformed.toString()
                    && q.Type === ProductionPlanQuestionType.SingleSelect);

            if (organicProductionStateQuestionIndex === -1
                || !farmBaseData.Questions[organicProductionStateQuestionIndex].Value
                || farmBaseData.Questions[organicProductionStateQuestionIndex].Value === OrganicProductionState.None.toString()) {
                enqueueSnackbar(t('AnelmaCattleProductionPlan:1075'), { variant: 'error' });
                setFarmBaseDataErrors((prev) => { return { ...prev, organicProductionError: true } });
                setIsLoading(false);
                return false;
            }
        }

        setFarmBaseDataErrors((prev) => { return { ...prev, organicProductionError: false } });
        setIsLoading(false);
        return true;
    };

    const isBuysCalfsDirectlyFromOtherFarmValid = () => {
        let i = farmBaseData.Questions.findIndex(q => q.ResourceId === QuestionResourceText.BuysCalfsDirectlyFromOtherFarm.toString());

        if (i != -1 && farmBaseData.Questions[i].Value === '1') {
            if (farmBaseData.SellingFarms.every((_) => _.Flags === 1) || farmBaseData.SellingFarms.length < 1) {
                enqueueSnackbar(t('AnelmaCattleProductionPlan:1078'), { variant: 'error' });
                setFarmBaseDataErrors((prev) => { return { ...prev, calfBuyError: true } });
                setIsLoading(false);
                return false;
            }
        }

        setFarmBaseDataErrors((prev) => { return { ...prev, calfBuyError: false } });
        setIsLoading(false);
        return true;
    };

    const requiredFarmBaseAnswersSet = () => {
        let i = farmBaseData.Questions.findIndex(q => q.ResourceId === QuestionResourceText.HasSalmonellaInsurance.toString());

        if (i != -1 && !farmBaseData.Questions[i].Value) {
            enqueueSnackbar(t('AnelmaCattleProductionPlan:1090'), { variant: 'error' });
            setFarmBaseDataErrors((prev) => { return { ...prev, salmonellaSampleError: true } });
            setIsLoading(false);
            return false;
        }

        i = farmBaseData.Questions.findIndex(q => q.ResourceId === QuestionResourceText.SoftSleepingPad.toString());

        if (i != -1 && !farmBaseData.Questions[i].Value) {
            enqueueSnackbar(t('AnelmaCattleProductionPlan:1089'), { variant: 'error' });
            setFarmBaseDataErrors((prev) => { return { ...prev, salmonellaSampleError: false, sleepingPadError: true } });
            setIsLoading(false);
            return false;
        }

        setFarmBaseDataErrors((prev) => { return { ...prev, salmonellaSampleError: false, sleepingPadError: false } });
        setIsLoading(false);
        return true;
    }

    const isFarmTrendsOpened = () => {
        if (farmTrendsOpened === false && props.isNew && props.isNew === true) {
            enqueueSnackbar(t('AnelmaLayout:1086'), { variant: 'error' });
            setFarmTrendsError(true);
            setIsLoading(false);
            return false;
        }

        setFarmTrendsError(false);
        setIsLoading(false);
        return true;
    };

    const isAnimalCapacitiesValid = () => {
        if (!farmDetails) return false;
        setCapacityError(false);

        let farmingTypes = farmDetails.ProductionLines.find(
            (x) => x.Type === AnimalTypeEnum.Bovine
        )?.FarmingTypes;

        if (animalData.AnimalCapacities.filter((x) => x.Capacity > 0).length === 0) {
            enqueueSnackbar(t('AnelmaCattleProductionPlan:1082'), { variant: 'error' });
            setCapacityError(true);
            setIsLoading(false);
            return false;
        }

        // lypsykarjatila
        if (farmingTypes?.includes(FarmingTypeEnum.Dairy)) {
            const dairyCowCapacity = animalData.AnimalCapacities.find(
                (x) => x.AnimalType === ProductionPlanAnimalType.DairyCow
            )?.Capacity;

            if (dairyCowCapacity !== undefined && dairyCowCapacity > 0) {
                return true;
            }
        }

        // emolehm�tila
        if (farmingTypes?.includes(FarmingTypeEnum.Calving)) {
            const breedCowCapacity = animalData.AnimalCapacities.find(
                (x) => x.AnimalType === ProductionPlanAnimalType.BreedingCow
            )?.Capacity;

            if (breedCowCapacity !== undefined && breedCowCapacity > 0) {
                return true;
            }
        }

        // kasvattaja
        if (farmingTypes?.includes(FarmingTypeEnum.Upbringing)) {
            const bullCapacity = animalData.AnimalCapacities.find(
                (x) => x.AnimalType === ProductionPlanAnimalType.Bull
            )?.Capacity;
            const heiferCapacity = animalData.AnimalCapacities.find(
                (x) => x.AnimalType === ProductionPlanAnimalType.Heifer
            )?.Capacity;

            if (bullCapacity !== undefined && bullCapacity > 0) {
                return true;
            }
            if (heiferCapacity !== undefined && heiferCapacity > 0) {
                return true;
            }
        }

        // v�likasvattaja
        if (farmingTypes?.includes(FarmingTypeEnum.Transmission)) {
            const calfCapacity = animalData.AnimalCapacities.find(
                (x) => x.AnimalType === ProductionPlanAnimalType.Calf
            )?.Capacity;

            if (calfCapacity !== undefined && calfCapacity > 0) {
                return true;
            }
        }

        enqueueSnackbar(t('AnelmaCattleProductionPlan:1082'), { variant: 'error' });
        setCapacityError(true);
        setIsLoading(false);
        return false;
    };

    const getDialogTitle = () => {
        return data.Id != null
            ? `${t('AnelmaLayout:1065')} ${props.data.TargetYear ?? ''}`
            : `${t('AnelmaCattleProductionPlan:1079')} ${props.data.TargetYear ?? ''}`;
    };
    const openFarmTrends = (isOpened: boolean) => {
        setFarmTrendsOpened(isOpened);
    };

    return (
        <ADialog open onClose={props.onClose} disableEnforceFocus>
            <ADialogTitle>
                {getDialogTitle()}
                <CloseDialogButton
                    className='animal-announcement-close-button-float-right'
                    onClick={() => props.onClose()}
                />
            </ADialogTitle>
            <ADialogContent size={'xl'} isLoading={isLoading}>
                {access === null || farmDetails === null ? (
                    <></>
                ) : (
                    <>
                        <PlanHeaderInfo data={data} farmDetails={farmDetails} />

                        <AnimalDataGrids
                            data={animalData}
                            setData={(data: IPlanAnimalData) => setAnimalData(data)}
                            access={access}
                            readonly={readonly}
                            validator={validator}
                            capacityError={capacityError}
                        />

                        <BasicFarmInfo
                            data={farmBaseData}
                            setData={(data) => { setFarmBaseData(data) }}
                            productionPlan={data}
                            access={access}
                            readonly={readonly}
                            validator={validator}
                            farmDetails={farmDetails}
                            errors={farmBaseDataErrors}
                        />

                        <FarmTrends
                            data={data}
                            farmTrends={farmTrends}
                            setFarmTrends={setFarmTrends}
                            access={access}
                            readonly={readonly}
                            farmTrendsError={farmTrendsError}
                            farmTrendsOpened={openFarmTrends}
                            isNew={props.isNew || false}
                        />

                        <Environment
                            data={environmentQuestions}
                            setData={(newQuestions) => setEnvironmentQuestions(newQuestions)}
                            readonly={readonly}
                            validator={validator}
                        />

                        <EndingOfProduction
                            data={endingOfProductionQuestions[0]}
                            setData={(q: IFarmQuestion) => { setEndingOfProductionQuestions([q])}}
                            readonly={readonly}
                            validator={validator}
                            currentTargetYear={props.data.TargetYear}
                        />

                        <Confirmation
                            data={confirmationData}
                            setData={(data: IPlanConfirmationData) => setConfirmationData(data)}
                            access={access}
                            readonly={readonly}
                            validator={validator}
                            onSave={handleSaveButtonClick}
                        />
                    </>
                )}
            </ADialogContent>
        </ADialog>
    );
}
